import React, { useState, useEffect } from 'react';
import api from '../../services/api';

function SalesAgentAssignmentModal({ isOpen, onClose, projectId, salesLeader, onSuccess }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchAvailableAgents();
    }
  }, [isOpen, projectId]);

  const fetchAvailableAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/design-projects/${projectId}/sales-agents/available`);
      setAgents(response.data.agents || []);
    } catch (err) {
      console.error('Error fetching available agents:', err);
      setError('Failed to load available agents');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (agentId, isPrimary = false) => {
    try {
      setSubmitting(true);
      await api.post(`/design-projects/${projectId}/sales-agents`, {
        agentId,
        isPrimary
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error assigning agent:', err);
      setError(err.response?.data?.error || 'Failed to assign agent');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Assign Sales Agent
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {salesLeader
                  ? `Select an agent from ${salesLeader.nickname || salesLeader.fullName}'s team`
                  : 'Select a sales agent to assign to this project'
                }
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : agents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-sm">No available sales agents found</p>
              {salesLeader && (
                <p className="text-xs mt-1">in {salesLeader.nickname || salesLeader.fullName}'s team</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {agents.map(agent => (
                <div
                  key={agent.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    agent.isAssigned
                      ? 'bg-gray-50 border-gray-200'
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-50 border border-green-200 flex items-center justify-center text-green-800">
                      <span className="text-sm font-medium">
                        {(agent.nickname || agent.fullName || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {agent.nickname || agent.fullName}
                      </p>
                      <p className="text-xs text-gray-500">{agent.email}</p>
                      {agent.leader && (
                        <p className="text-xs text-gray-400">
                          Team: {agent.leader.nickname || agent.leader.fullName}
                        </p>
                      )}
                    </div>
                  </div>
                  {agent.isAssigned ? (
                    <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                      Already Assigned
                    </span>
                  ) : (
                    <button
                      onClick={() => handleAssign(agent.id)}
                      disabled={submitting}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50 transition-colors"
                    >
                      {submitting ? 'Assigning...' : 'Assign'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default SalesAgentAssignmentModal;
